import { Form, Stack } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import IExtractClient from "../../../../models/ExtractClient";
import { ExtractClientStatus, ExtractClientStatusLabels, MisType } from "../../../../app/enums";
import MISButton from "../../../../components/button";
import { MatchForExtractState } from "../../../../models/DataMatchContext";

function getExtractSettingsDisplay(misType: MisType, settingsJson: string | undefined): string {
    try {
        const settings = JSON.parse(settingsJson || "{}");
        if (misType === MisType.Sims) {
            return settings.Username || "";
        }
        if (misType === MisType.Bromcom) {
            return settings.SchoolID || "";
        }
        if (misType === MisType.Arbor) {
            return settings.ApiCredentials?.EstablishmentUrl || "";
        }
    } catch {
        /* ignore */
    }
    return "";
}

interface BeginDataMatchModalProps {
    establishmentId: string | undefined;
    selectedMisConfigId: number | null;
    onSelectedMisConfigIdChange: (id: number | null) => void;
    eligibleClients: IExtractClient[];
    onClose: () => void;
}

export default function BeginDataMatchModal({ establishmentId, selectedMisConfigId, onSelectedMisConfigIdChange, eligibleClients, onClose }: BeginDataMatchModalProps) {
    const navigate = useNavigate();

    const handleConfirm = () => {
        if (selectedMisConfigId === null || establishmentId === undefined) {
            return;
        }
        const client = eligibleClients.find((c) => c.clientConfiguration.misConfigurationID === selectedMisConfigId);
        if (!client) {
            return;
        }
        const matchForExtract: MatchForExtractState = {
            establishmentId: String(establishmentId),
            misConfigurationID: client.clientConfiguration.misConfigurationID,
            misType: client.misType,
            clientID: String(client.clientID),
            providerLabel: MisType[client.misType]?.toUpperCase() ?? "Provider",
            extractStatus: client.clientConfiguration.status ?? ExtractClientStatus.Paused,
        };
        navigate(`/establishment/${establishmentId}/data-match`, { state: { matchForExtract }, replace: true });
        onClose();
    };

    return (
        <Stack gap={3}>
            <p className="text-center mb-0">
                Starting Data Match will pause the active MIS for this establishment so incoming provider data can be matched safely.
            </p>
            <div>
                <Form.Label className="fw-semibold">Which extract are you completing the data match for?</Form.Label>
                <Form.Select
                    value={selectedMisConfigId === null ? "" : String(selectedMisConfigId)}
                    onChange={(e) => {
                        const v = e.target.value;
                        onSelectedMisConfigIdChange(v === "" ? null : Number(v));
                    }}
                    required
                >
                    <option value="" disabled>
                        Select an extract…
                    </option>
                    {eligibleClients.map((c) => {
                        const id = c.clientConfiguration.misConfigurationID;
                        const label = MisType[c.misType]?.toUpperCase() ?? "MIS";
                        const statusLabel = ExtractClientStatusLabels[c.clientConfiguration.status ?? ExtractClientStatus.Paused];
                        const hint = getExtractSettingsDisplay(c.misType, c.clientConfiguration.settingsJson);
                        const suffix = hint ? ` — ${hint}` : "";
                        return (
                            <option key={id} value={String(id)}>
                                {label} ({statusLabel}){suffix}
                            </option>
                        );
                    })}
                </Form.Select>
                {eligibleClients.length === 0 && (
                    <p className="text-muted small mt-2 mb-0">No extracts are in Paused or Ingest. Set an extract to Paused or Ingest to continue.</p>
                )}
            </div>
            <Stack direction="horizontal" className="mt-1">
                <MISButton text="Cancel" className="popup-btn right-margin10 btn btn-outline-secondary w-50" FnOnClick={onClose} />
                <MISButton
                    text="Confirm"
                    className="popup-btn right-margin10 btn btn-primary crbc-bg-color w-50"
                    FnOnClick={handleConfirm}
                    disabled={selectedMisConfigId === null}
                />
            </Stack>
        </Stack>
    );
}
